import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Layout,
  Card,
  Filters,
  ResourceList,
  Text,
  Select,
  Banner,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams, Outlet } from "react-router-dom";
import { RootState, AppDispatch } from "configureStore";
import { fetchCustomers, setFilterValue } from "./customers.slice";
import Currency from "components/format/currency/currency";
import useIsPhone from "features/files/use-is-phone";
import UserSelect from "features/user-select/user-select";
import DataTable from "components/datatable/Datatable";
import { excludeCustomerToUser } from "services/user-select.service";

type FiltersValue = {
  textSearch?: string | null;
  user?: string | null;
  hide?: string | null;
};

type Customer = {
  id: string;
  name: string;
  assignedUser?: { name: string };
  _overdueDebt: number;
  _turnover: number;
  isHide?: boolean;
};

function Customers() {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const { companyId, userId } = useParams<{
    companyId: string;
    userId?: string;
  }>();

  const { customersCollection, filtersValue } = useSelector(
    (state: RootState) => state.customerHideReducer
  );

  const users: Record<string, string> = useSelector(
    (state: RootState) => state.customerHideReducer.users || {}
  );

  const disambiguateLabel = (key: keyof FiltersValue, value: string) => {
    switch (key) {
      case "textSearch":
        return `${t("search")}: ${t(value)}`;
      case "user":
        return `${t("assigned_user")} ${users[value]}`;
      default:
        return value;
    }
  };

  useEffect(() => {
    if (companyId) {
      dispatch(fetchCustomers(companyId, filtersValue, userId!));
    }
  }, [dispatch, filtersValue, companyId, userId]);

  const handleFilterChange = (value: any, key: keyof FiltersValue) => {
    dispatch(setFilterValue({ key, value }));
  };
  const handleQueryChange = (value: string) => {
    dispatch(setFilterValue({ key: "textSearch", value }));
  };
  const handleQueryValueRemove = () => {
    dispatch(setFilterValue({ key: "textSearch", value: "" }));
  };
  const handleClearAll = () => {};

  // The server flips the current state on every call, so a second tap on a row
  // that is still saving would undo the first one: ignore taps on such rows.
  // While a row is saving, its box shows the state that was asked for (customer id -> checked),
  // so it does not flicker back when the list reloads before the save is finished.
  const [requestedChecked, setRequestedChecked] = useState<
    Map<string, boolean>
  >(new Map());
  const [saveFailed, setSaveFailed] = useState(false);
  // A save can finish after the person has typed a newer search: the reload after it asks for what is searched
  // now, so it also replaces the load that the newer search started instead of discarding it.
  const currentFiltersValue = useRef(filtersValue);
  currentFiltersValue.current = filtersValue;

  const handleCheckboxClick = async (item: Customer) => {
    if (requestedChecked.has(item.id)) return;
    setRequestedChecked((requested) =>
      new Map(requested).set(item.id, !item.isHide)
    );
    setSaveFailed(false);
    try {
      await excludeCustomerToUser(companyId, { user: userId, customer: item.id });
      if (companyId && userId) {
        await dispatch(fetchCustomers(companyId, currentFiltersValue.current, userId));
      }
    } catch {
      setSaveFailed(true);
    } finally {
      setRequestedChecked((requested) => {
        const rest = new Map(requested);
        rest.delete(item.id);
        return rest;
      });
    }
  };

  const appliedFilters = Object.keys(filtersValue).reduce<
    { key: string; label: string; onRemove: () => void }[]
  >((acc, key) => {
    const value = filtersValue[key as keyof FiltersValue];
    if (value && value.length > 0) {
      acc.push({
        key,
        label: disambiguateLabel(key as keyof FiltersValue, value),
        onRemove: () => handleFilterChange(null, key as keyof FiltersValue),
      });
    }
    return acc;
  }, []);

  const filters = [
    {
      key: "hide",
      label: t("hide"),
      filter: (
        <Select
          label="hide"
          options={[
            { label: "-", value: "" },
            { label: "checked", value: "checked" },
          ]}
          onChange={(val) => handleFilterChange(val, "hide")}
          value={filtersValue.hide || ""}
        />
      ),
    },
    {
      key: "user",
      label: t("assigned_user"),
      filter: (
        <UserSelect
          value={filtersValue.user || ""}
          name="user"
          onChange={handleFilterChange}
          isRequired={false}
          allowMultiple={true}
          label={"select_user"}
        />
      ),
    },
  ];

  let content = <Outlet />;

  const filtersNode = (
    <Filters
      queryValue={filtersValue.textSearch || ""}
      filters={filters}
      appliedFilters={appliedFilters}
      onQueryChange={handleQueryChange}
      onQueryClear={handleQueryValueRemove}
      onClearAll={handleClearAll}
    />
  );

  content = (
    <Card>
      {saveFailed && (
        <Banner tone="critical" onDismiss={() => setSaveFailed(false)}>
          {t("not_saved")}
        </Banner>
      )}
      {isPhone ? (
        <ResourceList
          resourceName={{ singular: t("customer"), plural: t("customers") }}
          items={customersCollection}
          filterControl={filtersNode}
          flushFilters
          renderItem={(item: Customer) => {
            const { assignedUser, name, _overdueDebt, _turnover } = item;

            // The checkbox is the only tap target that changes the access, so
            // a scroll or a tap on the text never toggles it by accident.
            return (
              <li key={item.id} className="flex items-start gap-1 px-2 py-1">
                <label className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center">
                  <input
                    type="checkbox"
                    className="h-6 w-6 rounded border-gray-300"
                    aria-label={`${t("is_hide")}: ${name}`}
                    checked={requestedChecked.get(item.id) ?? !!item.isHide}
                    disabled={requestedChecked.has(item.id)}
                    onChange={() => handleCheckboxClick(item)}
                  />
                </label>
                <div className="min-w-0 flex-1 py-2.5">
                  <Text variant="bodyMd" fontWeight="bold" as="h3">
                    {name}
                  </Text>
                  <div>{assignedUser?.name || "-"}</div>
                  <div className="flex justify-space">
                    <div>
                      {_turnover > 0 && (
                        <Text as="p" alignment="start">
                          <Currency isSymbol={true} value={_turnover} />
                        </Text>
                      )}
                    </div>
                    <div>
                      {_overdueDebt > 0 && (
                        <Text as="p" alignment="end">
                          {t("debt")}:{" "}
                          <Currency isSymbol={true} value={_overdueDebt} />
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          }}
        />
      ) : (
        <div>
          <div style={{ padding: "16px", display: "flex" }}>
            <div style={{ flex: 1 }}>{filtersNode}</div>
          </div>
          <DataTable
            collection={customersCollection}
            columns={[
              { key: "#" },
              {
                key: "isHide",
                name: t("is_hide"),
                type: "checkbox",
                onClick: (item: any) => handleCheckboxClick(item),
              },
              { key: "name", name: t("title") },
              { key: "externalCode", name: t("external_code") },
              { key: "assignedUser.name", name: t("assigned_user") },
              {
                key: "_turnover",
                type: "currency",
                name: t("turnoverWithoutVat"),
              },
              { key: "_debt", type: "currency", name: t("debtWithVat") },
              {
                key: "_overdueDebt",
                type: "currency",
                name: t("overdue_debt"),
              },
            ]}
          />
        </div>
      )}
    </Card>
  );

  return (
    <Layout>
      <Layout.Section>{content}</Layout.Section>
    </Layout>
  );
}

export default Customers;
